import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { aegisApi, clearStoredTokens, getStoredToken, setStoredTokens } from 'api/aegis';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => getStoredToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));

  const loadCurrentUser = useCallback(async () => {
    if (!getStoredToken()) {
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setUser(await aegisApi.me());
    } catch {
      clearStoredTokens();
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser]);

  const login = useCallback(async (credentials) => {
    const tokens = await aegisApi.login(credentials);
    setStoredTokens(tokens);
    setToken(tokens.access_token);
    setUser(await aegisApi.me());
  }, []);

  const register = useCallback(async (account) => {
    const tokens = await aegisApi.register(account);
    setStoredTokens(tokens);
    setToken(tokens.access_token);
    setUser(await aegisApi.me());
  }, []);

  const logout = useCallback(() => {
    clearStoredTokens();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(token),
      loading,
      login,
      logout,
      register,
      refreshUser: loadCurrentUser,
      token,
      user
    }),
    [loadCurrentUser, loading, login, logout, register, token, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
